import { useState, useRef, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  newThreadId,
  listThreads,
  refreshThreads,
  fetchThread,
  loadThread as loadThreadFromStore,
  saveThread,
  renameThread,
  deleteThread,
  type ThreadMeta,
} from '../../lib/threads';
import type { Block, Message } from './types';
import { getActiveYear, getActiveInstitution, getActiveDivision, getPinnedStudent, getPinnedCohort, getPinnedCompany } from '../AppRail';

/**
 * Tiny pub/sub for "this thread is currently streaming" so the rail can
 * paint a typing indicator next to the right row even when the user is on
 * another page. We can't use storage events cleanly because the value
 * lives only for the duration of a single request — fire a custom event
 * on `window` instead and let listeners read the current value off the
 * exported `streamingState` ref.
 */
export const streamingState: { activeThreadId: string | null } = { activeThreadId: null };
export const STREAMING_EVENT = 'fb-streaming-change';
function setStreamingThread(id: string | null) {
  streamingState.activeThreadId = id;
  try { window.dispatchEvent(new CustomEvent(STREAMING_EVENT, { detail: id })); } catch {/* SSR */}
}

/**
 * Scan a restored thread's messages for the id of the most recent testCard
 * block. Used to rehydrate the "last test" reference on thread load/reopen so
 * a follow-up "assign it to …" resolves after a page reload (the reference is
 * otherwise only set live when a testCard streams in during this session).
 */
function lastTestIdFromMessages(msgs: any[]): string | null {
  if (!Array.isArray(msgs)) return null;
  let found: string | null = null;
  for (const m of msgs) {
    if (m?.role === 'agent' && Array.isArray(m.blocks)) {
      for (const b of m.blocks) {
        if (b?.kind === 'testCard' && b?.testId) found = b.testId;
      }
    }
  }
  return found;
}

const STARTER_REPLIES: Record<string, { title: string; steps: Block[] }> = {
  readiness: {
    title: 'Cohort readiness · Google SWE',
    steps: [
      { kind: 'tool', label: "Loading CSE '26 cohort", state: 'done' },
      { kind: 'tool', label: 'Benchmarking against 5-year Google OA patterns', state: 'done' },
      { kind: 'text', text: "Here's the readiness snapshot for your 184-student CSE '26 cohort against the Google SWE drive pattern. DSA is strong (top quartile tier tracking 78% OA pass rate), but system-design depth lags — only 12% of the top quartile have demonstrated scalability reasoning beyond caching." },
      { kind: 'suggestions', options: ['Generate a 4-week practice track', 'Show per-student gaps', 'Compare with last year'] },
    ],
  },
  shortlist: {
    title: 'Shortlist · Razorpay SDE I',
    steps: [
      { kind: 'tool', label: 'Parsing requirement', state: 'done' },
      { kind: 'tool', label: 'Applying exclusion rules', state: 'done' },
      { kind: 'tool', label: 'Ranking 184 students', state: 'done' },
      { kind: 'text', text: "Ranked 42 eligible candidates for Razorpay SDE I (8 target hires). Top 20 cleared all three filters — CGPA ≥ 7.5, no existing tier-1 offer, and passing scores on the hiring patterns Razorpay used in 2023–2024. Aarav Sharma and Nikhil Reddy lead on combined profile strength." },
      { kind: 'suggestions', options: ['Show full ranked list', 'Export as Excel', 'Add interview slot prep'] },
    ],
  },
  conflicts: {
    title: 'Offer conflicts',
    steps: [
      { kind: 'tool', label: 'Scanning 14 pending offers', state: 'done' },
      { kind: 'tool', label: 'Applying dream-offer rules', state: 'done' },
      { kind: 'text', text: "3 conflicts flagged this week. Priya Menon holds 2 offers (Flipkart + dream-tier Google) — dream rule auto-releases Flipkart. Two double-placements detected in ECE: both need manual review since neither tier is dream-protected." },
      { kind: 'suggestions', options: ['Open conflict detail', 'Notify affected students', 'Review dream-offer rules'] },
    ],
  },
};

const API_BASE =
  (import.meta as unknown as { env: Record<string, string> }).env.VITE_API_URL ?? 'http://localhost:3000';
const FRONTEND_KEY =
  (import.meta as unknown as { env: Record<string, string> }).env.VITE_FRONTEND_KEY ?? '';

export interface ChatEngine {
  messages: Message[];
  threadTitle: string | null;
  threadId: string | null;
  threads: ThreadMeta[];
  running: boolean;
  ask: (text: string) => void;
  send: (text: string) => void;
  startFlow: (id: string, userText: string) => void;
  reset: () => void;
  uploadFile: (file: File) => void;
  /** Record an ImportWizard upload/import in the chat thread. */
  importNote: (evt: { kind: 'start' | 'done' | 'cancel'; fileName: string; summary?: string }) => void;
  openThread: (id: string) => void;
  renameCurrentThread: (title: string) => Promise<void>;
  deleteCurrentThread: () => Promise<void>;
  /** Cancel the in-flight reply (marks any streaming text as final, leaves
   * the user message in place so the user can retry). */
  stop: () => void;
  /** Re-run the last user message — used when the previous reply errored. */
  retryLast: () => void;
}

export function useChatEngine(): ChatEngine {
  const [searchParams, setSearchParams] = useSearchParams();

  // Hydrate from URL synchronously on first render so a hard refresh of
  // /dashboard?thread=xxx renders the thread immediately — without this,
  // we briefly paint the welcome screen before the post-mount effect
  // swaps in the loaded thread, which the user sees as a flash.
  const initialThreadId = searchParams.get('thread');
  const initialThread = initialThreadId ? loadThreadFromStore(initialThreadId) : null;
  const initialMessages: Message[] = initialThread
    ? (initialThread.messages as any[]).map((m) =>
        m.role === 'user'
          ? { role: 'user' as const, text: m.text, at: m.at }
          : { role: 'agent' as const, blocks: m.blocks, at: m.at },
      )
    : [];

  const [messages, setMessages] = useState<Message[]>(initialMessages);
  const [threadTitle, setThreadTitle] = useState<string | null>(initialThread?.title ?? null);
  const [threadId, setThreadId] = useState<string | null>(initialThread?.id ?? null);
  const [threads, setThreads] = useState<ThreadMeta[]>(() => listThreads());
  const [running, setRunning] = useState(false);

  // On mount: refresh thread index from server, and pull the active thread's
  // server copy (in case it was updated from another device or only exists
  // in the DB and not in this browser's cache).
  useEffect(() => {
    refreshThreads().then(setThreads).catch(() => {/* keep cache */});
    if (initialThreadId) {
      fetchThread(initialThreadId).then((t) => {
        if (!t) return;
        setMessages((t.messages as any[]).map((m) =>
          m.role === 'user'
            ? { role: 'user' as const, text: m.text, at: m.at }
            : { role: 'agent' as const, blocks: m.blocks, at: m.at },
        ));
        setThreadTitle(t.title);
        // Critical: setThreadId so header Pin/Share render. Cache-miss case
        // would otherwise leave threadId null even though the URL has one.
        setThreadId(t.id);
        batchIdRef.current = t.batchId;
        lastTestIdRef.current = lastTestIdFromMessages(t.messages as any[]);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const runIdRef = useRef(0);
  const batchIdRef = useRef<string | null>(initialThread?.batchId ?? null);
  // Last test created in this thread, echoed back to the server so
  // "assign it to …" resolves even across PM2 cluster workers (the server's
  // in-memory thread→test map is per-process and can't be relied on in prod).
  const lastTestIdRef = useRef<string | null>(null);

  const syncUrl = (id: string | null) => {
    const next = new URLSearchParams(searchParams);
    if (id) next.set('thread', id);
    else next.delete('thread');
    setSearchParams(next, { replace: true });
  };

  const persist = (opts: { id: string; title: string; msgs: Message[] }) => {
    saveThread({
      id: opts.id,
      title: opts.title,
      updatedAt: Date.now(),
      messages: opts.msgs.map((m) =>
        m.role === 'user'
          ? { role: 'user', text: m.text, at: m.at }
          : { role: 'agent', blocks: m.blocks, at: m.at }
      ),
      batchId: batchIdRef.current,
    });
    setThreads(listThreads());
  };

  const updateLastAgent = (updater: (m: Extract<Message, { role: 'agent' }>) => Extract<Message, { role: 'agent' }>) => {
    setMessages((msgs) => {
      const copy = [...msgs];
      for (let i = copy.length - 1; i >= 0; i--) {
        if (copy[i].role === 'agent') {
          copy[i] = updater(copy[i] as Extract<Message, { role: 'agent' }>);
          break;
        }
      }
      return copy;
    });
  };

  const playBlocks = async (blocks: Block[]) => {
    const id = ++runIdRef.current;
    setRunning(true);
    setMessages((m) => [...m, { role: 'agent', blocks: [], at: Date.now() }]);
    await new Promise((r) => setTimeout(r, 150));

    for (const b of blocks) {
      if (runIdRef.current !== id) return;

      if (b.kind === 'tool') {
        updateLastAgent((a) => ({ ...a, blocks: [...a.blocks, { ...b, state: 'running' as const }] }));
        await new Promise((r) => setTimeout(r, 700));
        updateLastAgent((a) => ({
          ...a,
          blocks: a.blocks.map((blk, idx) =>
            idx === a.blocks.length - 1 && blk.kind === 'tool' ? { ...blk, state: 'done' as const } : blk
          ),
        }));
      } else if (b.kind === 'text') {
        const full = b.text;
        updateLastAgent((a) => ({ ...a, blocks: [...a.blocks, { kind: 'text' as const, text: '', streaming: true }] }));
        const chunks = 30;
        const per = Math.ceil(full.length / chunks);
        for (let i = 1; i <= chunks; i++) {
          if (runIdRef.current !== id) return;
          const partial = full.slice(0, i * per);
          updateLastAgent((a) => ({
            ...a,
            blocks: a.blocks.map((blk, idx) =>
              idx === a.blocks.length - 1 && blk.kind === 'text' ? { kind: 'text' as const, text: partial, streaming: true } : blk
            ),
          }));
          await new Promise((r) => setTimeout(r, 25));
        }
        updateLastAgent((a) => ({
          ...a,
          blocks: a.blocks.map((blk, idx) =>
            idx === a.blocks.length - 1 && blk.kind === 'text' ? { kind: 'text' as const, text: full, streaming: false } : blk
          ),
        }));
      } else if (b.kind === 'suggestions') {
        updateLastAgent((a) => ({ ...a, blocks: [...a.blocks, b] }));
      }
    }
    setRunning(false);
  };

  const startFlow = (id: string, userText: string) => {
    const flow = STARTER_REPLIES[id];
    if (!flow) return;
    runIdRef.current++;
    setMessages([{ role: 'user', text: userText, at: Date.now() }]);
    setThreadTitle(flow.title);
    playBlocks(flow.steps);
  };

  const callChatApi = async (afterMessages: Message[], tid?: string, title?: string) => {
    const id = ++runIdRef.current;
    setRunning(true);
    if (tid) setStreamingThread(tid);
    setMessages((m) => [...m, { role: 'agent', blocks: [], at: Date.now() }]);

    const token = typeof window !== 'undefined' ? localStorage.getItem('auth_token') : null;

    const history = afterMessages.map((m) => {
      if (m.role === 'user') return { role: 'user', content: m.text };
      const textBlocks = m.blocks.filter((b) => b.kind === 'text') as Extract<Block, { kind: 'text' }>[];
      return { role: 'assistant', content: textBlocks.map((b) => b.text).join('\n\n') };
    });

    try {
      console.log('[fb] chat ➜', { history, batchId: batchIdRef.current });
      const res = await fetch(`${API_BASE}/api/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(FRONTEND_KEY ? { 'X-Frontend-Key': FRONTEND_KEY } : {}),
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          messages: history,
          context: {
            ...(batchIdRef.current ? { batchId: batchIdRef.current } : {}),
            ...(lastTestIdRef.current ? { lastTestId: lastTestIdRef.current } : {}),
            // Active scope chips from the topbar — the server uses these
            // as implicit defaults when the user doesn't explicitly name
            // a year / institution ("top 10 students" → top 10 in this
            // batch + this campus, not across everything). The pinned
            // student is similar but stronger: until the chip's X is
            // clicked, every query is interpreted as "about that student".
            activeYear: getActiveYear(),
            activeInstitution: getActiveInstitution(),
            activeDivision: getActiveDivision(),
            pinnedStudent: getPinnedStudent(),
            pinnedCohort: getPinnedCohort(),
            pinnedCompany: getPinnedCompany(),
          },
          threadId: tid ?? null,
        }),
      });
      if (!res.ok) {
        // Stale/expired JWT — bounce to login with a banner reason
        // instead of dumping a confusing "NetworkError" into the chat.
        if (res.status === 401) {
          if (typeof window !== 'undefined') {
            localStorage.removeItem('auth_token');
            const next = encodeURIComponent(window.location.pathname + window.location.search);
            window.location.replace(`/auth/logout?reason=expired&next=${next}`);
          }
          return;
        }
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || err.error || res.statusText);
      }
      const data = await res.json() as { blocks: any[] };
      console.log('[fb] chat ⬅', data);

      for (const b of data.blocks) {
        if (runIdRef.current !== id) return;
        if (b.kind === 'tool' && b.label) {
          updateLastAgent((a) => ({ ...a, blocks: [...a.blocks, { kind: 'tool' as const, label: b.label!, state: 'running' as const }] }));
          await new Promise((r) => setTimeout(r, 400));
          updateLastAgent((a) => ({
            ...a,
            blocks: a.blocks.map((blk, idx) =>
              idx === a.blocks.length - 1 && blk.kind === 'tool' ? { ...blk, state: 'done' as const } : blk
            ),
          }));
        } else if (b.kind === 'text' && b.text) {
          // Word-by-word typewriter — feels smoother than fixed-stride
          // chunks because new text always lands on a word boundary.
          // Cadence is ~22 ms/word with a tiny extra beat after sentence
          // terminators so the eye has a chance to settle. Long replies
          // get a faster cadence so the user doesn't wait through 8s of
          // streaming.
          const full = b.text;
          updateLastAgent((a) => ({ ...a, blocks: [...a.blocks, { kind: 'text' as const, text: '', streaming: true }] }));
          // Tokenise on whitespace but keep the spaces with the prior token
          // so partial text reads naturally as it grows.
          const tokens = full.match(/\S+\s*/g) ?? [full];
          const baseDelay = tokens.length > 80 ? 14 : 22;
          let acc = '';
          for (let i = 0; i < tokens.length; i++) {
            if (runIdRef.current !== id) return;
            acc += tokens[i];
            updateLastAgent((a) => ({
              ...a,
              blocks: a.blocks.map((blk, idx) =>
                idx === a.blocks.length - 1 && blk.kind === 'text' ? { kind: 'text' as const, text: acc, streaming: true } : blk
              ),
            }));
            // Brief extra pause after sentence-ending punctuation so the
            // narrative breathes — feels less like a wall of words.
            const trailingPunct = /[.!?…][\s"')\]]*\s*$/.test(tokens[i]);
            await new Promise((r) => setTimeout(r, baseDelay + (trailingPunct ? 90 : 0)));
          }
          updateLastAgent((a) => ({
            ...a,
            blocks: a.blocks.map((blk, idx) =>
              idx === a.blocks.length - 1 && blk.kind === 'text' ? { kind: 'text' as const, text: full, streaming: false } : blk
            ),
          }));
        } else if (b.kind === 'mapping') {
          updateLastAgent((a) => ({
            ...a,
            blocks: [...a.blocks, { ...(b as any), kind: 'mapping' as const }],
          }));
          await new Promise((r) => setTimeout(r, 220));
        } else if (b.kind === 'issues') {
          updateLastAgent((a) => ({ ...a, blocks: [...a.blocks, { ...(b as any), kind: 'issues' as const, issues: b.issues || [] }] }));
          await new Promise((r) => setTimeout(r, 220));
        } else if (b.kind === 'students') {
          // Spread the server block through FIRST so we never silently drop a
          // field the backend added (per-row skillPct/_reasons, studentColumns,
          // etc.). The explicit keys below only supply safe defaults. The
          // canonical column field is `studentColumns` — an earlier rebuild
          // mis-stored it as `columns`, which the renderer ignores, so the lens
          // (incl. the skill-% column) silently vanished on every saved thread.
          updateLastAgent((a) => ({
            ...a,
            blocks: [...a.blocks, {
              ...(b as any),
              kind: 'students' as const,
              students: b.students || [],
              allStudents: b.allStudents || b.students || [],
              total: b.total ?? 0,
              studentColumns: (b as any).studentColumns ?? (b as any).columns,
            }],
          }));
          await new Promise((r) => setTimeout(r, 220));
        } else if (b.kind === 'aggregation') {
          updateLastAgent((a) => ({ ...a, blocks: [...a.blocks, { ...(b as any), kind: 'aggregation' as const }] }));
          await new Promise((r) => setTimeout(r, 220));
        } else if (b.kind === 'companies'
                || b.kind === 'companyDossier'
                || b.kind === 'companyComparison'
                || b.kind === 'interviewQuestions'
                || b.kind === 'intelligence'
                || b.kind === 'eligibilityMatch'
                || b.kind === 'report-card') {
          // Each rich card gets a brief "preparing" loader so the card
          // doesn't pop in instantly the moment the response lands. Feels
          // more like a colleague pulling up a dossier than a database
          // dump. Hold ~1.6s, then slide the card down slowly (the card
          // root has duration-700+ animation classes).
          const loaderLabel = (() => {
            switch (b.kind) {
              case 'companies':          return 'Composing the company list';
              case 'companyDossier':     return 'Preparing the dossier';
              case 'companyComparison':  return 'Building the comparison';
              case 'interviewQuestions': return 'Surfacing interview questions';
              case 'intelligence':       return 'Compiling intelligence';
              case 'eligibilityMatch':   return 'Matching eligibility cutoffs';
              case 'report-card':        return 'Generating report';
              default:                   return 'Composing answer';
            }
          })();
          updateLastAgent((a) => ({ ...a, blocks: [...a.blocks, { kind: 'tool' as const, label: loaderLabel, state: 'running' as const }] }));
          await new Promise((r) => setTimeout(r, 1600));
          if (runIdRef.current !== id) return;
          updateLastAgent((a) => ({
            ...a,
            blocks: a.blocks.map((blk, idx) =>
              idx === a.blocks.length - 1 && blk.kind === 'tool'
                ? { ...blk, state: 'done' as const }
                : blk,
            ),
          }));
          // Tiny breath after the loader resolves before the card slides
          // down — gives the eye a moment to read the green tick.
          await new Promise((r) => setTimeout(r, 180));
          if (runIdRef.current !== id) return;

          // All rich cards: spread the server block through so nested payloads
          // (dossier/comparison/intel/eligibility/report-card fields) and any
          // future additions are preserved verbatim — no field-by-field rebuild.
          if (b.kind === 'companies') {
            updateLastAgent((a) => ({
              ...a,
              blocks: [...a.blocks, { ...(b as any), kind: 'companies' as const, companies: b.companies ?? [], total: b.total ?? 0 }],
            }));
          } else if (b.kind === 'companyDossier') {
            updateLastAgent((a) => ({
              ...a,
              blocks: [...a.blocks, { ...(b as any), kind: 'companyDossier' as const }],
            }));
          } else if (b.kind === 'companyComparison') {
            updateLastAgent((a) => ({
              ...a,
              blocks: [...a.blocks, { ...(b as any), kind: 'companyComparison' as const }],
            }));
          } else if (b.kind === 'interviewQuestions') {
            updateLastAgent((a) => ({
              ...a,
              blocks: [...a.blocks, { ...(b as any), kind: 'interviewQuestions' as const }],
            }));
          } else if (b.kind === 'intelligence') {
            updateLastAgent((a) => ({
              ...a,
              blocks: [...a.blocks, { ...(b as any), kind: 'intelligence' as const }],
            }));
          } else if (b.kind === 'eligibilityMatch') {
            updateLastAgent((a) => ({
              ...a,
              blocks: [...a.blocks, { ...(b as any), kind: 'eligibilityMatch' as const }],
            }));
          } else if (b.kind === 'report-card') {
            updateLastAgent((a) => ({
              ...a,
              blocks: [...a.blocks, { ...(b as any), kind: 'report-card' as const }],
            }));
          }
          // Allow the slide-down animation to play before the next block
          // (suggestions chips, etc.) starts mounting.
          await new Promise((r) => setTimeout(r, 280));
        } else if (b.kind === 'studentCard') {
          updateLastAgent((a) => ({
            ...a,
            blocks: [...a.blocks, { ...(b as any), kind: 'studentCard' as const, title: b.title ?? '' }],
          }));
          await new Promise((r) => setTimeout(r, 220));
        } else if (b.kind === 'testCard') {
          // Track the most recent test so a follow-up "assign it to …" can
          // reference it via context.lastTestId (cluster-safe; see the ref decl).
          if ((b as any).testId) lastTestIdRef.current = (b as any).testId;
          updateLastAgent((a) => ({
            ...a,
            blocks: [...a.blocks, { ...(b as any), kind: 'testCard' as const }],
          }));
          await new Promise((r) => setTimeout(r, 220));
        } else if (b.kind === 'planCard') {
          updateLastAgent((a) => ({
            ...a,
            blocks: [...a.blocks, { ...(b as any), kind: 'planCard' as const, testIds: (b as any).testIds ?? [], days: (b as any).days ?? [] }],
          }));
          await new Promise((r) => setTimeout(r, 220));
        } else if (b.kind === 'assignTest') {
          updateLastAgent((a) => ({
            ...a,
            blocks: [...a.blocks, { ...(b as any), kind: 'assignTest' as const }],
          }));
          await new Promise((r) => setTimeout(r, 220));
        } else if (b.kind === 'actions') {
          updateLastAgent((a) => ({
            ...a,
            blocks: [...a.blocks, { ...(b as any), kind: 'actions' as const, items: (b as any).items ?? [] }],
          }));
          await new Promise((r) => setTimeout(r, 220));
        } else if (b.kind === 'evidence') {
          // Folded "reasoning & sources" + profile/company link under an answer.
          updateLastAgent((a) => ({ ...a, blocks: [...a.blocks, { ...(b as any), kind: 'evidence' as const, citations: (b as any).citations ?? [] }] }));
        } else if (b.kind === 'suggestions') {
          updateLastAgent((a) => ({ ...a, blocks: [...a.blocks, { ...(b as any), kind: 'suggestions' as const, options: b.options || [] }] }));
        }
      }
    } catch (e: any) {
      console.error('[fb] chat error', e);
      updateLastAgent((a) => ({
        ...a,
        blocks: [...a.blocks, { kind: 'text' as const, text: `**Chat error:** ${e.message || 'unknown error'}` }],
      }));
    } finally {
      // Safety net — if the response completed without producing any
      // renderable blocks, the agent message would still show
      // ThinkingDots forever (blocks.length === 0 is the same condition
      // as "still streaming"). Inject a retry-friendly fallback so the
      // user can click ↻ Retry instead of staring at the dots.
      updateLastAgent((a) => {
        if (a.blocks.length > 0) return a;
        return {
          ...a,
          blocks: [{
            kind: 'text' as const,
            text: '**Hmm, that came back empty.** The server didn\'t return anything I could render — click **↻ Retry last message** below to try again, or rephrase your request.',
          }],
        };
      });
      setRunning(false);
      setStreamingThread(null);
      if (tid) {
        setMessages((latest) => {
          persist({ id: tid, title: title ?? (latest[0]?.role === 'user' ? latest[0].text.slice(0, 40) : 'Thread'), msgs: latest });
          return latest;
        });
      }
    }
  };

  const ensureThread = (seedTitle: string) => {
    if (threadId) return threadId;
    const id = newThreadId();
    setThreadId(id);
    setThreadTitle(seedTitle);
    syncUrl(id);
    return id;
  };

  const ask = (text: string) => {
    runIdRef.current++;
    const next: Message[] = [{ role: 'user' as const, text, at: Date.now() }];
    setMessages(next);
    const title = text.slice(0, 40);
    const id = ensureThread(title);
    setThreadTitle(title);
    persist({ id, title, msgs: next });
    callChatApi(next, id, title);
  };

  const send = (text: string) => {
    const next: Message[] = [...messages, { role: 'user' as const, text, at: Date.now() }];
    setMessages(next);
    const title = threadTitle || text.slice(0, 40);
    const id = ensureThread(title);
    persist({ id, title, msgs: next });
    callChatApi(next, id, title);
  };

  const reset = () => {
    runIdRef.current++;
    setRunning(false);
    setMessages([]);
    setThreadTitle(null);
    setThreadId(null);
    batchIdRef.current = null;
    syncUrl(null);
  };

  const openThread = (id: string) => {
    const apply = (t: { id: string; title: string; messages: any[]; batchId: string | null }) => {
      runIdRef.current++;
      setRunning(false);
      const restored: Message[] = (t.messages as any[]).map((m) =>
        m.role === 'user'
          ? { role: 'user' as const, text: m.text, at: m.at }
          : { role: 'agent' as const, blocks: m.blocks, at: m.at }
      );
      setMessages(restored);
      setThreadTitle(t.title);
      setThreadId(t.id);
      batchIdRef.current = t.batchId;
      lastTestIdRef.current = lastTestIdFromMessages(t.messages as any[]);
      syncUrl(id);
    };
    const cached = loadThreadFromStore(id);
    if (cached) apply(cached);
    // Even on cache hit, re-fetch from server in case another device updated it.
    fetchThread(id).then((t) => { if (t) apply(t); });
  };

  /**
   * Post a chat trace for an import done through the ImportWizard modal (which
   * otherwise leaves no conversation record). 'start' opens/records an ingest
   * thread ("📎 Uploaded X — extracting…"); 'done'/'cancel' close it out with a
   * summary. Keeps the chat as the source of truth for what happened.
   */
  const importNote = (evt: { kind: 'start' | 'done' | 'cancel'; fileName: string; summary?: string }) => {
    const title = `Ingest · ${evt.fileName}`;
    if (evt.kind === 'start') {
      runIdRef.current++;
      setThreadTitle(title);
      const tid = ensureThread(title);
      const msgs: Message[] = [
        { role: 'user', text: `📎 Uploaded ${evt.fileName}`, at: Date.now() },
        { role: 'agent', blocks: [{ kind: 'tool', label: `Extracting & mapping ${evt.fileName}…`, state: 'running' }], at: Date.now() },
      ];
      setMessages(msgs);
      persist({ id: tid, title, msgs });
      return;
    }
    // done / cancel — mark the running tool done and append the outcome.
    updateLastAgent((a) => ({ ...a, blocks: a.blocks.map((b) => (b.kind === 'tool' ? { ...b, state: 'done' as const } : b)) }));
    const text = evt.kind === 'done'
      ? `✓ **Imported ${evt.fileName}.** ${evt.summary ?? ''}`.trim()
      : `Import of **${evt.fileName}** was cancelled — nothing was saved.`;
    setMessages((m) => [...m, { role: 'agent', blocks: [{ kind: 'text', text }], at: Date.now() }]);
    const tid = threadId ?? ensureThread(title);
    setMessages((latest) => { persist({ id: tid, title, msgs: latest }); return latest; });
  };

  const uploadFile = async (file: File) => {
    runIdRef.current++;
    const title = `Ingest · ${file.name}`;
    setMessages([{ role: 'user', text: `📎 Uploaded ${file.name}`, at: Date.now() }]);
    setThreadTitle(title);
    const tid = ensureThread(title);
    setRunning(true);
    setMessages((m) => [...m, { role: 'agent', blocks: [{ kind: 'tool', label: `Reading ${file.name}`, state: 'running' }] }]);

    try {
      const form = new FormData();
      form.append('file', file);
      const token = typeof window !== 'undefined' ? localStorage.getItem('auth_token') : null;

      console.log('[fb] upload start', { fileName: file.name, size: file.size, hasToken: !!token });
      const t0 = performance.now();
      const res = await fetch(`${API_BASE}/api/ingest/upload`, {
        method: 'POST',
        headers: {
          ...(FRONTEND_KEY ? { 'X-Frontend-Key': FRONTEND_KEY } : {}),
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: form,
      });
      console.log(`[fb] upload response ${res.status} in ${(performance.now() - t0).toFixed(0)}ms`);

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        console.warn('[fb] upload failed', err);
        throw new Error(err.message || err.error || res.statusText);
      }

      const data = await res.json();
      console.log('[fb] upload ok', data);
      batchIdRef.current = data.batchId;

      updateLastAgent((a) => ({
        ...a,
        blocks: a.blocks.map((b) => b.kind === 'tool' ? { ...b, state: 'done' as const } : b),
      }));

      const sampleCols = data.headers.slice(0, 6).join(', ') + (data.headers.length > 6 ? `, +${data.headers.length - 6} more` : '');
      await playBlocks([
        { kind: 'tool', label: `Parsed sheet "${data.sheetName}" · ${data.rowCount} rows`, state: 'done' },
        { kind: 'text', text: `Got it — **${data.fileName}** parsed (${data.headers.length} columns: ${sampleCols}). Analyzing the mapping…` },
      ]);
      // Auto-run the mapping straight away — no need to ask the user to say
      // "propose mapping". batchId is already in context (batchIdRef), so the
      // server routes to propose_mapping and returns a review card with
      // Confirm / Cancel buttons.
      await callChatApi([{ role: 'user' as const, text: 'propose mapping', at: Date.now() }], tid, title);
    } catch (e: any) {
      updateLastAgent((a) => ({
        ...a,
        blocks: a.blocks.map((b) => b.kind === 'tool' ? { ...b, state: 'done' as const } : b),
      }));
      await playBlocks([
        { kind: 'text', text: `**Upload failed:** ${e.message || 'unknown error'}.\n\nCheck that the file is a valid .xlsx / .xls and try again.` },
      ]);
    } finally {
      setRunning(false);
      setMessages((latest) => {
        persist({ id: tid, title, msgs: latest });
        return latest;
      });
    }
  };

  const urlThread = searchParams.get('thread');
  useEffect(() => {
    if (urlThread && urlThread !== threadId) {
      openThread(urlThread);
    } else if (!urlThread && threadId) {
      setMessages([]);
      setThreadId(null);
      setThreadTitle(null);
      batchIdRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlThread]);

  const renameCurrentThread = async (title: string) => {
    if (!threadId) return;
    const t = title.trim();
    if (!t) return;
    setThreadTitle(t);
    await renameThread(threadId, t);
    setThreads(listThreads());
  };

  const deleteCurrentThread = async () => {
    if (!threadId) return;
    const id = threadId;
    // Reset local state first so the UI flips to the welcome screen even if
    // the network round-trip lags.
    runIdRef.current++;
    setRunning(false);
    setMessages([]);
    setThreadTitle(null);
    setThreadId(null);
    batchIdRef.current = null;
    syncUrl(null);
    await deleteThread(id);
    setThreads(listThreads());
  };

  // Cancel the in-flight reply. We bump runIdRef so any pending chunk
  // dispatch becomes a no-op, finalize any half-streamed text, and tag the
  // last agent message with a stopped marker (text-block "(stopped)") so
  // the UI can show the action row + retry.
  const stop = () => {
    if (!running) return;
    runIdRef.current++;
    setRunning(false);
    setStreamingThread(null);
    setMessages((msgs) => {
      const copy = [...msgs];
      for (let i = copy.length - 1; i >= 0; i--) {
        const m = copy[i];
        if (m.role !== 'agent') continue;
        const blocks = m.blocks.map((b) =>
          b.kind === 'text' && b.streaming ? { ...b, streaming: false } : b,
        );
        // If nothing was streamed at all, drop a placeholder so the row isn't empty.
        const hasContent = blocks.some((b) => b.kind === 'text' || b.kind === 'students' || b.kind === 'mapping' || b.kind === 'aggregation' || b.kind === 'issues' || b.kind === 'studentCard');
        copy[i] = {
          ...m,
          blocks: hasContent ? blocks : [...blocks, { kind: 'text' as const, text: '_(stopped)_' }],
        };
        break;
      }
      return copy;
    });
  };

  // Re-send the most recent user message. Useful when a reply errored.
  const retryLast = () => {
    const lastUser = [...messages].reverse().find((m) => m.role === 'user');
    if (lastUser && lastUser.role === 'user') send(lastUser.text);
  };

  return { messages, threadTitle, threadId, threads, running, ask, send, startFlow, reset, uploadFile, importNote, openThread, renameCurrentThread, deleteCurrentThread, stop, retryLast };
}
